import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Briefcase, ArrowRight, X, Upload } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import PageHero from "@/components/site/PageHero";
import MagneticButton from "@/components/site/MagneticButton";
import { getJob, submitApplication } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const TYPES = { full_time: "Full-time", contract: "Contract", intern: "Internship" };

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", cover_letter: "" });
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setJob(null);
    getJob(slug).then(setJob).catch(() => setJob({ notFound: true }));
  }, [slug]);

  useSEO({
    title: job?.title ? `${job.title} — Careers at RockLadder` : "Open Role",
    description: job?.description_md?.slice(0, 160),
    canonical: `https://rockladder.com/careers/${slug}`,
    jsonLd: job && !job.notFound && {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: job.description_md,
      employmentType: (TYPES[job.type] || job.type).toUpperCase(),
      hiringOrganization: { "@type": "Organization", name: "RockLadder Technologies" },
      jobLocation: { "@type": "Place", address: job.location },
      datePosted: job.published_at,
    },
  });

  if (job?.notFound) return <div className="pt-40 pb-24 text-center"><Link to="/careers" className="text-gold">Back to Careers</Link></div>;
  if (!job) return <div className="pt-40 pb-24 text-center text-inkText-muted">Loading…</div>;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("job_slug", slug);
      fd.append("name", form.name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      if (form.linkedin) fd.append("linkedin", form.linkedin);
      if (form.cover_letter) fd.append("cover_letter", form.cover_letter);
      if (resume) fd.append("resume", resume);
      await submitApplication(fd);
      toast.success("Application received. Our team will be in touch.");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", linkedin: "", cover_letter: "" });
      setResume(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Application failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow={`${job.department.toUpperCase()} · ${(TYPES[job.type] || job.type).toUpperCase()}`}
        title={job.title}
        subtitle={job.description_md}
        breadcrumb={[{ label: "Careers", to: "/careers" }, { label: job.title }]}
      />

      <section className="relative py-12">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="flex flex-wrap items-center gap-5 mb-10 text-sm text-inkText-muted">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" /> {job.location}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-gold" /> {TYPES[job.type] || job.type}</span>
            <span className="font-mono text-[11px] tracking-widest">{job.experience}</span>
          </div>

          <div className="space-y-12">
            <div>
              <h2 className="font-display text-xl text-inkText-primary mb-4">Responsibilities</h2>
              <ul className="space-y-2.5">
                {job.responsibilities.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm text-inkText-muted leading-relaxed">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />{r}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-display text-xl text-inkText-primary mb-4">Requirements</h2>
              <ul className="space-y-2.5">
                {job.requirements.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm text-inkText-primary/90 leading-relaxed">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-violet2 flex-shrink-0" />{r}
                  </li>
                ))}
              </ul>
            </div>
            {job.nice_to_have?.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-inkText-primary mb-4">Nice to have</h2>
                <ul className="space-y-2.5">
                  {job.nice_to_have.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm text-inkText-muted leading-relaxed">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-inkText-muted flex-shrink-0" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-14">
            <MagneticButton
              onClick={() => setOpen(true)}
              variant="primary"
              data-testid="apply-open"
            >
              Apply now <ArrowRight className="h-4 w-4" />
            </MagneticButton>
          </div>
        </div>
      </section>

      {/* Apply modal */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-ink/85 backdrop-blur-md" onClick={() => setOpen(false)} />
            <motion.form
              onSubmit={submit}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.35 }}
              data-testid="apply-modal"
              className="relative w-full max-w-lg glass-panel-strong rounded-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto"
            >
              <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 text-inkText-muted hover:text-gold" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
              <div className="font-display text-xl md:text-2xl text-inkText-primary mb-1">Apply — {job.title}</div>
              <p className="text-xs text-inkText-muted mb-6">We read every application. No auto-rejects.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { k: "name", label: "Full name *", type: "text", required: true },
                  { k: "email", label: "Email *", type: "email", required: true },
                  { k: "phone", label: "Phone", type: "tel" },
                  { k: "linkedin", label: "LinkedIn", type: "url" },
                ].map((f) => (
                  <label key={f.k} className="block">
                    <span className="block font-mono text-[10px] tracking-[0.24em] text-inkText-muted mb-1.5">{f.label.toUpperCase()}</span>
                    <input
                      type={f.type}
                      required={f.required}
                      value={form[f.k]}
                      onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))}
                      data-testid={`apply-${f.k}`}
                      className="w-full rounded-lg bg-ink/60 border border-gold/20 px-3 py-2.5 text-sm text-inkText-primary focus:outline-none focus:border-gold"
                    />
                  </label>
                ))}
              </div>

              <label className="block mt-3">
                <span className="block font-mono text-[10px] tracking-[0.24em] text-inkText-muted mb-1.5">COVER LETTER</span>
                <textarea
                  rows={4}
                  value={form.cover_letter}
                  onChange={(e) => setForm((p) => ({ ...p, cover_letter: e.target.value }))}
                  data-testid="apply-cover"
                  className="w-full rounded-lg bg-ink/60 border border-gold/20 px-3 py-2.5 text-sm text-inkText-primary focus:outline-none focus:border-gold"
                />
              </label>

              <label className="block mt-3">
                <span className="block font-mono text-[10px] tracking-[0.24em] text-inkText-muted mb-1.5">RESUME (PDF, DOC, DOCX · 5 MB max)</span>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-gold/25 hover:border-gold text-inkText-primary hover:text-gold px-4 py-2.5 text-sm transition-colors">
                    <Upload className="h-4 w-4" />
                    <span>{resume ? resume.name.slice(0, 24) : "Choose file"}</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={(e) => setResume(e.target.files?.[0] || null)}
                      data-testid="apply-resume"
                      className="hidden"
                    />
                  </label>
                </div>
              </label>

              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-gold/20 text-inkText-muted hover:text-inkText-primary px-4 py-2 text-sm">Cancel</button>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="apply-submit"
                  className="rounded-full bg-gradient-to-b from-[#FFD37A] to-[#C89026] text-ink px-5 py-2 text-sm font-medium border border-gold/60 disabled:opacity-50"
                >
                  {busy ? "Submitting…" : "Submit application"}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
